import { Box, Paper, Typography, FormControlLabel, Checkbox, Button, Stack } from '@mui/material';
import Calendar from './Calendar';
import MultiSelectChips from './MultiSelectChips';
import { useToast } from '@/context/ToastContext';
import { findLatestDateForTicker } from '@/hooks/useFilterOptions';
import type { ViewpointFilters } from '@/types';

interface FilterBarProps {
  filters: ViewpointFilters;
  onFiltersChange: (filters: ViewpointFilters) => void;
  availableDates: string[];
  availableTickers: string[];
  availableChannels: string[];
}

// Ticker presets
const TICKER_PRESETS = [
  {
    id: 'mag10',
    label: '大科技',
    tickers: ['AAPL', 'MSFT', 'GOOGL', 'AMZN', 'NVDA', 'META', 'TSLA', 'AVGO', 'AMD', 'NFLX'],
  },
  {
    id: 'crypto',
    label: '加密',
    tickers: ['BTC', 'ETH', 'COIN', 'MSTR', 'RIOT', 'MARA', 'IBIT'],
  },
  {
    id: 'nuclear',
    label: '核电',
    tickers: ['URA', 'CCJ', 'SMR', 'UUUU', 'UEC', 'LEU'],
  },
];

export default function FilterBar({
  filters,
  onFiltersChange,
  availableDates,
  availableTickers,
  availableChannels,
}: FilterBarProps) {
  const { showToast } = useToast();

  const updateFilter = <K extends keyof ViewpointFilters>(key: K, value: ViewpointFilters[K]) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const applyPreset = (preset: typeof TICKER_PRESETS[0]) => {
    const newTickers = [...new Set([...filters.tickers, ...preset.tickers])];
    updateFilter('tickers', newTickers);
  };

  const clearAllFilters = () => {
    onFiltersChange({
      date: null,
      ticker: null,
      channel: null,
      dateRange: null,
      tickers: [],
      channels: [],
      onlyExplicit: false,
    });
  };

  const handleUnknownTicker = async (ticker: string) => {
    const latestDate = await findLatestDateForTicker(ticker);
    if (latestDate) {
      // Update filters: set date and add ticker
      onFiltersChange({
        ...filters,
        date: latestDate,
        tickers: [...new Set([...filters.tickers, ticker])],
      });
      showToast(`已跳转到 ${ticker} 最近的日期: ${latestDate}`, 'success');
    } else {
      showToast(`${ticker} 没有收录`, 'warning');
    }
  };

  return (
    <Paper sx={{ p: 2, mb: 3 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '280px 1fr' }, gap: 3 }}>
        {/* Calendar Section */}
        <Box>
          <Typography variant="subtitle2" gutterBottom sx={{ color: 'text.secondary' }}>
            日期选择
          </Typography>
          <Calendar
            selected={filters.date}
            onChange={(date) => updateFilter('date', date)}
            available={availableDates}
          />
        </Box>

        {/* Filters Section */}
        <Box>
          <Stack spacing={2}>
            {/* Ticker Filter */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                  股票代码
                </Typography>
                {TICKER_PRESETS.map((preset) => (
                  <Button
                    key={preset.id}
                    size="small"
                    variant="outlined"
                    onClick={() => applyPreset(preset)}
                    sx={{ fontSize: '0.7rem', py: 0, minHeight: 24 }}
                  >
                    +{preset.label}
                  </Button>
                ))}
              </Box>
              <MultiSelectChips
                options={availableTickers}
                value={filters.tickers}
                onChange={(tickers) => updateFilter('tickers', tickers)}
                placeholder="搜索股票代码..."
                onUnknownInput={handleUnknownTicker}
              />
            </Box>

            {/* Channel Filter */}
            <Box>
              <Typography variant="subtitle2" gutterBottom sx={{ color: 'text.secondary' }}>
                博主/频道
              </Typography>
              <MultiSelectChips
                options={availableChannels}
                value={filters.channels}
                onChange={(channels) => updateFilter('channels', channels)}
                placeholder="搜索博主..."
              />
            </Box>

            {/* Options */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filters.onlyExplicit}
                    onChange={(e) => updateFilter('onlyExplicit', e.target.checked)}
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2">仅显示明确提及的股票</Typography>
                }
              />
              <Button
                size="small"
                onClick={clearAllFilters}
                sx={{ color: 'text.secondary' }}
              >
                清除筛选
              </Button>
            </Box>
          </Stack>
        </Box>
      </Box>
    </Paper>
  );
}
